import {
  DAY_HOUR_TIME_PARSE_FORMAT,
  SIMPLIFIED_TWELVE_HOUR_TIME_PARSE_FORMAT,
} from 'features/store/constants';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import {
  getNextEarliestOpeningHours,
  isStoreClosingWithinMinutes,
  readableCloseHourToday,
  readableHour,
} from 'features/store/utils';

import { IRestaurant } from '@rbi-ctg/store';
import { t } from 'state/intl/translations';

interface IOpenClosedText {
  isOpen: boolean;
  isWithinOneHourOfClose: boolean;
  text: string;
}

export const useOpenClosedText = (store: IRestaurant): IOpenClosedText => {
  const futureFormatConfig = DAY_HOUR_TIME_PARSE_FORMAT;
  const { getPickupOpenServiceModes } = useStoreAvailabilityFns();

  const openPickupServiceModes = getPickupOpenServiceModes(store);

  // this means that the store is not open for pickup at all
  if (!openPickupServiceModes.length) {
    const nextEarliestOpeningTimes = getNextEarliestOpeningHours({
      store,
      onlyPickupServiceModes: true,
    });

    if (!nextEarliestOpeningTimes) {
      return {
        isOpen: false,
        isWithinOneHourOfClose: false,
        text: t('closed'),
      };
    }

    const { open: openingTime, nextOpen: nextOpeningTime } = nextEarliestOpeningTimes;

    const timeFormat =
      store?.localTime != null && openingTime != null && store.localTime > openingTime
        ? futureFormatConfig
        : SIMPLIFIED_TWELVE_HOUR_TIME_PARSE_FORMAT;

    const timeISOString =
      store?.localTime != null && openingTime != null && store.localTime > openingTime
        ? nextOpeningTime
        : openingTime ?? nextOpeningTime;

    if (!timeISOString) {
      return {
        isOpen: false,
        isWithinOneHourOfClose: false,
        text: `${t('closed')}`,
      };
    }

    const readable = readableHour({
      timeISOString,
      timeFormat,
      storeTimeZone: store.timezone ?? '',
    });
    return {
      isOpen: false,
      isWithinOneHourOfClose: false,
      text: `${t('closed')} - ${t('opensAt', {
        time: readable ?? t('notApplicable'),
      })}`,
    };
  }

  const isClosingWithinNextHour = isStoreClosingWithinMinutes(store, 60);

  if (isClosingWithinNextHour) {
    return {
      isOpen: true,
      isWithinOneHourOfClose: true,
      text: t('closesAt', {
        time: readableCloseHourToday(store, SIMPLIFIED_TWELVE_HOUR_TIME_PARSE_FORMAT),
      }),
    };
  }

  return {
    isOpen: true,
    isWithinOneHourOfClose: false,
    text: t('open'),
  };
};
